/** Branch performance PDF with CSS bar charts */
import { resolveBranchForPrint } from './branchPrint';

const esc = (v) =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const money = (n) =>
  `Rs. ${Number(n || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const exportBranchPerformancePdf = (data = {}, options = {}) => {
  const branchFilter = resolveBranchForPrint(options);
  const period = data.period?.label || options.periodLabel || '';
  const branches = data.branches || [];
  const maxVal = Math.max(
    1,
    ...branches.map((b) => Math.max(b.actual_sales || 0, b.target_amount || 0))
  );

  const barRows = branches
    .map((b) => {
      const actualPct = Math.min(100, ((b.actual_sales || 0) / maxVal) * 100);
      const targetPct = Math.min(100, ((b.target_amount || 0) / maxVal) * 100);
      const ach = b.achievement_pct || 0;
      return `
        <div class="bar-block">
          <div class="bar-head">
            <strong>${esc(b.branch_name)} (${esc(b.branch_code || '-')})</strong>
            <span class="${b.achieved ? 'ok' : 'warn'}">${ach.toFixed(1)}% ${b.achieved ? '✓ Target met' : '✗ Below target'}</span>
          </div>
          <div class="bar-meta">Actual ${esc(money(b.actual_sales))} · Target ${esc(money(b.target_amount))} · Bills ${b.bill_count || 0}</div>
          <div class="track"><div class="fill actual" style="width:${actualPct}%"></div></div>
          <div class="track target-track"><div class="fill target" style="width:${targetPct}%"></div></div>
          <div class="legend-mini"><span class="dot actual"></span> Actual &nbsp; <span class="dot target"></span> Target</div>
        </div>`;
    })
    .join('');

  const achieverRows = (data.achievers || [])
    .map(
      (b) =>
        `<tr><td>${esc(b.branch_name)}</td><td>${esc(b.branch_code)}</td><td class="right">${esc(money(b.actual_sales))}</td><td class="right">${esc(money(b.target_amount))}</td><td class="right ok">${b.achievement_pct}%</td></tr>`
    )
    .join('');

  const cashierRows = (data.top_cashiers || [])
    .map((c, i) => {
      const br = branches.find((b) => Number(b.branch_id) === Number(c.branch_id));
      return `<tr><td>${i + 1}</td><td>${esc(c.name)}</td><td>${esc(br?.branch_name || c.branch_id)}</td><td class="right">${c.bill_count}</td><td class="right">${esc(money(c.total_sales))}</td></tr>`;
    })
    .join('');

  const productRows = (data.top_products || [])
    .map(
      (p, i) =>
        `<tr><td>${i + 1}</td><td>${esc(p.product_name)}</td><td class="right">${p.qty}</td><td class="right">${esc(money(p.total_sales))}</td></tr>`
    )
    .join('');

  const company = data.company || {};
  const win = window.open('', '_blank', 'width=1100,height=800,scrollbars=yes');
  if (!win) {
    alert('Please allow pop-ups for PDF export');
    return;
  }
  const baseUrl = window.location.origin;
  win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Branch Sales Performance</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap');
  body{font-family:Poppins,Arial,sans-serif;font-size:11px;color:#222;margin:0;padding:18px 22px}
  .header{display:flex;justify-content:space-between;align-items:center;border-bottom:2px solid #1976d2;padding-bottom:10px;margin-bottom:12px}
  .logo{height:64px}
  .company{text-align:right}
  .company-name{font-weight:700;color:#1976d2;font-size:13px}
  .banner{display:flex;justify-content:space-between;gap:12px;background:linear-gradient(90deg,#e3f2fd,#f8fbff);border:1px solid #bbdefb;border-radius:8px;padding:12px 14px;margin-bottom:14px}
  .title{font-size:16px;font-weight:700;color:#1565c0;margin:0 0 4px}
  .badge{background:#1976d2;color:#fff;padding:8px 12px;border-radius:20px;font-weight:600;white-space:nowrap}
  .kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:14px}
  .kpi{border:1px solid #e0e0e0;border-radius:8px;padding:10px;background:#fafafa}
  .kpi .l{font-size:9px;color:#666;text-transform:uppercase}
  .kpi .v{font-size:13px;font-weight:700;margin-top:3px}
  .section{margin:16px 0 8px;font-size:13px;font-weight:700;color:#1565c0;border-bottom:1px solid #ddd;padding-bottom:4px}
  .bar-block{margin-bottom:12px;padding:8px;border:1px solid #eee;border-radius:6px}
  .bar-head{display:flex;justify-content:space-between;gap:8px;margin-bottom:4px}
  .bar-meta{color:#666;font-size:10px;margin-bottom:6px}
  .track{background:#eceff1;height:12px;border-radius:6px;overflow:hidden;margin-bottom:3px}
  .target-track{height:8px;opacity:.85}
  .fill{height:100%;border-radius:6px}
  .fill.actual{background:linear-gradient(90deg,#42a5f5,#1976d2)}
  .fill.target{background:linear-gradient(90deg,#ffb74d,#f57c00)}
  .legend-mini{font-size:9px;color:#777}
  .dot{display:inline-block;width:8px;height:8px;border-radius:50%;margin-right:3px}
  .dot.actual{background:#1976d2}.dot.target{background:#f57c00}
  .ok{color:#2e7d32;font-weight:700}.warn{color:#c62828;font-weight:700}
  table{width:100%;border-collapse:collapse;margin-top:6px}
  th{background:#1976d2;color:#fff;text-align:left;padding:7px 8px;font-size:10px}
  td{border-bottom:1px solid #eee;padding:6px 8px}
  tr:nth-child(even) td{background:#f8fbff}
  .right{text-align:right}
  .footer{margin-top:16px;text-align:center;color:#777;font-size:10px;border-top:1px dashed #ccc;padding-top:10px}
  .actions{margin-top:16px}
  .actions button{background:#1976d2;color:#fff;border:none;border-radius:4px;padding:8px 14px;font-weight:600;margin-right:8px;cursor:pointer}
  @media print{.no-print{display:none!important} body{padding:8mm}}
</style></head><body>
  <div class="header">
    <img class="logo" src="${baseUrl}/LOGOFile.png" onerror="this.style.display='none'" alt="Logo"/>
    <div class="company">
      <div class="company-name">Sun System ERP</div>
      <div>Branch Sales Targets & Performance</div>
    </div>
  </div>
  <div class="banner">
    <div>
      <h1 class="title">Branch Sales Performance Report</h1>
      <div>Period: ${esc(period)}</div>
      <div>Generated: ${esc(new Date().toLocaleString('en-GB'))}</div>
    </div>
    <div class="badge">${esc(branchFilter.label)}</div>
  </div>
  <div class="kpis">
    <div class="kpi"><div class="l">Company Target</div><div class="v">${esc(money(company.target_amount))}</div></div>
    <div class="kpi"><div class="l">Company Actual</div><div class="v">${esc(money(company.actual_sales))}</div></div>
    <div class="kpi"><div class="l">Achievement</div><div class="v">${esc((company.achievement_pct || 0) + '%')}</div></div>
    <div class="kpi"><div class="l">Branches Hit Target</div><div class="v">${(data.achievers || []).length} / ${branches.length}</div></div>
  </div>

  <div class="section">Target vs Actual (chart)</div>
  ${barRows || '<p>No branch data</p>'}

  <div class="section">Branches that achieved target</div>
  <table>
    <thead><tr><th>Branch</th><th>Code</th><th class="right">Actual</th><th class="right">Target</th><th class="right">%</th></tr></thead>
    <tbody>${achieverRows || '<tr><td colspan="5">No branch achieved target yet</td></tr>'}</tbody>
  </table>

  <div class="section">Top cashiers</div>
  <table>
    <thead><tr><th>#</th><th>Cashier</th><th>Branch</th><th class="right">Bills</th><th class="right">Sales</th></tr></thead>
    <tbody>${cashierRows || '<tr><td colspan="5">No cashier data</td></tr>'}</tbody>
  </table>

  <div class="section">Top products</div>
  <table>
    <thead><tr><th>#</th><th>Product</th><th class="right">Qty</th><th class="right">Sales</th></tr></thead>
    <tbody>${productRows || '<tr><td colspan="4">No product data</td></tr>'}</tbody>
  </table>

  <div class="footer">Sun System ERP · Confidential · ${esc(branchFilter.label)}</div>
  <div class="no-print actions">
    <button onclick="window.print()">Print / Save as PDF</button>
    <button onclick="window.close()" style="background:#607d8b">Close</button>
  </div>
  <script>window.onload=function(){setTimeout(function(){window.print()},400)}</script>
</body></html>`);
  win.document.close();
};
